import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, uploadFile } from "@/lib/api";
import { usePrefs } from "@/lib/context";

function Pad({ canvasRef, onDraw }) {
  const drawing = useRef(false);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (canvasRef.current.width / r.width), (e.clientY - r.top) * (canvasRef.current.height / r.height)];
  };
  const down = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    ctx.beginPath(); ctx.moveTo(...pos(e));
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(...pos(e)); ctx.stroke();
    onDraw();
  };
  return <canvas ref={canvasRef} width={560} height={200} onPointerDown={down} onPointerMove={move} onPointerUp={() => (drawing.current = false)}
    className="w-full touch-none rounded-sm border border-dashed border-slate-400 bg-white" data-testid="signature-canvas" />;
}

export function SignDialog({ doc }) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, 560, 200); setDirty(false); };
  const save = async () => {
    setBusy(true);
    try {
      const blob = await new Promise((r) => ref.current.toBlob(r, "image/png"));
      const { path } = await uploadFile(new File([blob], "signature.png", { type: "image/png" }));
      await api.post(`/documents/${doc.id}/sign`, { image_path: path });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success(t("signed"));
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setDirty(false); }}>
      <DialogTrigger asChild><Button size="sm" className="h-8 rounded-sm bg-[#0F172A] text-xs hover:bg-[#B8860B]" data-testid={`sign-button-${doc.id}`}><PenLine size={13} className="mr-1" />{t("sign")}</Button></DialogTrigger>
      <DialogContent className="max-w-xl rounded-sm" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("sign")} — {doc.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{t("signHere")}</p>
        <Pad canvasRef={ref} onDraw={() => setDirty(true)} />
        <div className="flex justify-between">
          <Button variant="outline" className="rounded-sm" onClick={clear} data-testid="signature-clear-button">{t("clear")}</Button>
          <Button disabled={!dirty || busy} onClick={save} className="rounded-sm bg-[#0F172A]" data-testid="signature-save-button">{busy && <Loader2 size={14} className="mr-1 animate-spin" />}{t("sign")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
